import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
   Check,
   X,
   ArrowRight,
   Smartphone,
   Cloud,
   Shield,
   BarChart3,
   FileText,
   Users,
   Star,
   ChevronDown,
   TrendingUp,
   PlayCircle,
   CheckCircle,
   Sparkles,
   Lock,
   Rocket,
   Clock,
   MessageCircle,
   Phone,
   Bot,
   Workflow,
   Brain,
   Scan,
   Fingerprint,
   PieChart,
   LineChart,
   BarChart,
   Cctv,
   ShieldCheck,
   Send,
   Zap,
   Shirt,
   Scissors,
   ChevronLeft,
   ChevronRight,
   Play,
   ArrowUp, // Added for back-to-top button
} from 'lucide-react';
import { Image } from '@chakra-ui/react';
import Dashboard from '../../assets/img/dashboard.png';
import Navigation from './ZeronineHeader';
import Footer from './ZeronineFooter';
import { useDispatch, useSelector } from 'react-redux';
import { getPackageForWeb } from '../../redux/actions/package';
import { Link } from 'react-router-dom';

export default function ZeroNineAccounting() {
   const dispatch = useDispatch();
   const [billingCycle, setBillingCycle] = useState('annual');
   const [scrolled, setScrolled] = useState(false);
   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
   const [darkMode, setDarkMode] = useState(false);
   const [aiAssistantOpen, setAiAssistantOpen] = useState(false); // You can remove this if not needed
   const [activeFeatureTab, setActiveFeatureTab] = useState('ai');
   const [selectedPlan, setSelectedPlan] = useState(null);
   const [hoveredPlan, setHoveredPlan] = useState(null);
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const [activeSection, setActiveSection] = useState('home');
   const [showBackToTop, setShowBackToTop] = useState(false); // State for back-to-top button

   const getCurrentPage = () => {
      const path = window.location.pathname;
      if (path.includes('/about')) return 'about';
      if (path.includes('/')) return 'home';
      return 'home';
   };

   const [currentPage, setCurrentPage] = useState(getCurrentPage());

   const PackageData = useSelector((state) => state.Package?.list || []);

   // Enhanced AI Features
   const [aiFeatures] = useState([
      {
         icon: Brain,
         title: 'AI-Powered Insights',
         description:
            'Get intelligent business recommendations and predictive analytics using machine learning algorithms',
         gradient: 'from-purple-500 to-pink-500',
         stats: '95% prediction accuracy',
         features: ['Predictive cash flow', 'Smart tax optimization', 'Revenue forecasting'],
      },
      {
         icon: Bot,
         title: 'Smart Automation',
         description: 'Automate repetitive tasks with intelligent workflows and RPA technology',
         gradient: 'from-blue-500 to-cyan-500',
         stats: 'Saves 20+ hours/month',
         features: ['Auto categorization', 'Invoice processing', 'Payment reminders'],
      },
      {
         icon: Scan,
         title: 'Document Intelligence',
         description: 'AI that reads and processes invoices, receipts, and documents with OCR technology',
         gradient: 'from-green-500 to-emerald-500',
         stats: '99.9% accuracy rate',
         features: ['Auto data extraction', 'Expense tracking', 'Document search'],
      },
      {
         icon: Workflow,
         title: 'Workflow Builder',
         description: 'Drag-and-drop interface to create custom automation workflows',
         gradient: 'from-orange-500 to-red-500',
         stats: 'No-code automation',
         features: ['Visual workflow editor', 'Conditional logic', 'Multi-step automation'],
      },
   ]);

   // Enhanced Security Features
   const [securityFeatures] = useState([
      {
         icon: Fingerprint,
         title: 'Biometric Authentication',
         description: 'Secure login with fingerprint and face recognition technology',
         gradient: 'from-red-500 to-orange-500',
         stats: 'Military-grade security',
         features: ['Face ID', 'Touch ID', 'Multi-factor auth'],
      },
      {
         icon: Shield,
         title: 'Blockchain Audit Trail',
         description: 'Immutable transaction records on distributed blockchain network',
         gradient: 'from-indigo-500 to-purple-500',
         stats: 'Tamper-proof records',
         features: ['Distributed ledger', 'Smart contracts', 'Transparent audit'],
      },
      {
         icon: Lock,
         title: 'Zero-Knowledge Encryption',
         description: 'Your data is encrypted end-to-end with zero-knowledge architecture',
         gradient: 'from-gray-500 to-blue-500',
         stats: 'Bank-level encryption',
         features: ['End-to-end encryption', 'Zero-knowledge proof', 'Client-side crypto'],
      },
      {
         icon: Cctv,
         title: 'Real-time Monitoring',
         description: '24/7 security monitoring with AI-powered threat detection',
         gradient: 'from-yellow-500 to-amber-500',
         stats: 'Instant threat alerts',
         features: ['Anomaly detection', 'Real-time alerts', 'Security dashboard'],
      },
   ]);

   // Advanced Analytics Features
   const [analyticsFeatures] = useState([
      {
         icon: LineChart,
         title: 'Real-time Analytics',
         description: 'Live business metrics and performance indicators with interactive dashboards',
         gradient: 'from-teal-500 to-cyan-500',
         stats: 'Live data streaming',
         features: ['Custom metrics', 'Real-time updates', 'Interactive charts'],
      },
      {
         icon: PieChart,
         title: 'Advanced Reporting',
         description: 'Deep insights with customizable reports and data visualization',
         gradient: 'from-violet-500 to-purple-500',
         stats: '50+ report templates',
         features: ['Custom reports', 'Data export', 'Scheduled reports'],
      },
      {
         icon: TrendingUp,
         title: 'Predictive Analytics',
         description: 'AI-driven forecasts and trend analysis for better decision making',
         gradient: 'from-rose-500 to-pink-500',
         stats: '90-day forecasts',
         features: ['Trend analysis', 'Seasonal patterns', 'Growth predictions'],
      },
   ]);

   const features = [
      {
         icon: FileText,
         title: 'Smart Invoicing',
         description: 'Create professional GST-compliant invoices instantly with AI-powered suggestions',
         gradient: 'from-blue-500 to-cyan-400',
         stats: '10K+ invoices created daily',
         advanced: true,
      },
      {
         icon: BarChart3,
         title: 'Real-time Analytics',
         description: 'Business insights at your fingertips with interactive dashboards and live data',
         gradient: 'from-purple-500 to-pink-400',
         stats: 'Live dashboard updates',
         advanced: true,
      },
      {
         icon: Cloud,
         title: 'Cloud-Based',
         description: 'Access anywhere, automatic backups with multi-cloud infrastructure',
         gradient: 'from-green-500 to-emerald-400',
         stats: '99.9% uptime guaranteed',
         advanced: true,
      },
      {
         icon: Shield,
         title: 'Bank-Level Security',
         description: '256-bit encryption protection with SOC 2 compliance',
         gradient: 'from-red-500 to-orange-400',
         stats: 'ISO certified security',
         advanced: true,
      },
      {
         icon: Smartphone,
         title: 'Mobile First',
         description: 'Powerful mobile app for iOS & Android with offline capabilities',
         gradient: 'from-indigo-500 to-blue-400',
         stats: '4.8★ app rating',
         advanced: true,
      },
      {
         icon: Users,
         title: 'Team Collaboration',
         description: 'Role-based access for your team with real-time collaboration',
         gradient: 'from-yellow-500 to-orange-400',
         stats: 'Unlimited team members',
         advanced: true,
      },
   ];

   const testimonials = [
      {
         name: 'Rajesh Kumar',
         role: 'CEO, Kumar Electronics',
         rating: 5,
         text: 'ZeroNine transformed our accounting! GST filing is now a breeze. The automation saved us 15+ hours weekly.',
         image: 'RK',
         company: 'Mumbai',
         revenue: '↑ 40% efficiency',
         aiUsage: 'Uses AI automation daily',
      },
      {
         name: 'Priya Sharma',
         role: 'Founder, Sharma Textiles',
         rating: 5,
         text: 'Best ROI of any software! Inventory management is flawless. Support team is incredibly responsive.',
         image: 'PS',
         company: 'Delhi',
         revenue: '↑ 3x growth',
         aiUsage: 'Loves predictive analytics',
      },
      {
         name: 'Amit Patel',
         role: 'Director, Patel Trading',
         rating: 5,
         text: 'Switched from expensive software to ZeroNine. Never looked back! Real-time insights are game-changing.',
         image: 'AP',
         company: 'Ahmedabad',
         revenue: '↓ 60% costs',
         aiUsage: 'Uses voice commands',
      },
   ];

   const faqs = [
      {
         q: 'Can I switch plans anytime?',
         a: 'Yes! Upgrade or downgrade anytime. Changes reflect immediately on your account.',
      },
      {
         q: 'Is my data secure?',
         a: 'Absolutely! We use bank-grade 256-bit encryption and are ISO 27001 certified with blockchain backup.',
      },
      {
         q: 'Do you offer migration support?',
         a: 'Yes! Our AI-powered migration tool and team will help you migrate all your data for free from any other platform.',
      },
      {
         q: 'What payment methods do you accept?',
         a: 'We accept all major credit/debit cards, UPI, net banking, digital wallets, and cryptocurrency.',
      },
      {
         q: 'Do you have AI features?',
         a: 'Yes! We offer advanced AI-powered insights, automation, document processing, and predictive analytics.',
      },
      {
         q: 'Can I use voice commands?',
         a: 'Absolutely! Our voice AI assistant understands natural language for creating invoices, reports, and more.',
      },
   ];

   const carouselRef = useRef(null);
   const [selectedBusinessType, setSelectedBusinessType] = useState('All');
   const [filteredPackages, setFilteredPackages] = useState([]);
   const activePackages = useMemo(() => {
      const uniquePackages = [];
      const seenNames = new Set();

      PackageData.filter((pkg) => pkg.status === true).forEach((pkg) => {
         if (!seenNames.has(pkg.name)) {
            seenNames.add(pkg.name);
            uniquePackages.push(pkg);
         }
      });

      return uniquePackages;
   }, [PackageData]);

   // Get available business types that actually have packages
   const getAvailableBusinessTypes = () => {
      const businessTypes = ['All', 'MOBILE', 'TEXTILE', 'EMBROIDERY', 'NORMAL'];
      return businessTypes.filter((businessType) => {
         if (businessType === 'All') return true;
         return activePackages.some((pkg) => pkg.businesstypename === businessType);
      });
   };

   useEffect(() => {
      let filtered = [];
      if (selectedBusinessType === 'All') {
         filtered = activePackages;
      } else {
         filtered = activePackages.filter((pkg) => pkg.businesstypename === selectedBusinessType);
      }

      setFilteredPackages(filtered);

      // Reset selected plan when filter changes - only select if there are packages
      if (filtered.length > 0) {
         setSelectedPlan(); // Select first package by default
      } else {
         setSelectedPlan(null); // No selection if no packages
      }
   }, [selectedBusinessType, activePackages]);

   // Handle plan selection
   const handlePlanSelect = (index) => {
      setSelectedPlan(index);
   };

   // Scroll functions
   const scrollPricing = (direction) => {
      if (carouselRef.current) {
         const scrollAmount = 400;
         carouselRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
         });
      }
   };

   const scrollToPricingCard = (index) => {
      if (carouselRef.current && filteredPackages[index]) {
         const card = carouselRef.current.children[index];
         card.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
         });
         handlePlanSelect(index);
      }
   };

   // Back to top function
   const scrollToTop = () => {
      window.scrollTo({
         top: 0,
         behavior: 'smooth',
      });
   };

   useEffect(() => {
      const handleScroll = () => {
         setScrolled(window.scrollY > 50);
         // Show back-to-top button when scrolled down 300px
         setShowBackToTop(window.scrollY > 300);
      };

      const handleMouseMove = (e) => {
         setMousePosition({ x: e.clientX, y: e.clientY });
      };

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('mousemove', handleMouseMove);

      return () => {
         window.removeEventListener('scroll', handleScroll);
         window.removeEventListener('mousemove', handleMouseMove);
      };
   }, []);

   useEffect(() => {
      dispatch(getPackageForWeb());
   }, [dispatch]);

   // Remove AI Assistant render function
   // const renderAIAssistant = () => <AIAssistant open={aiAssistantOpen} onClose={() => setAiAssistantOpen(false)} />;

   const renderAboutSection = () => (
      <section className="py-24 px-4 bg-white">
         <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
               {/* Left Side - Image with Frame */}
               <div className="relative">
                  {/* Green Frame */}
                  <div className="absolute -top-6 -left-6 w-full h-full border-8 border-blue-500 z-0"></div>

                  {/* Main Image */}
                  <div className="relative z-10 bg-gray-200 h-96">
                     <img
                        src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                        alt="Zeronine Accounting Software Team"
                        className="w-full h-full object-cover"
                     />

                     {/* Stats Badge */}
                     <div className="absolute bottom-0 right-0 bg-gray-800 text-white px-8 py-6 flex items-center gap-4">
                        <div className="text-center">
                           <div className="text-5xl font-bold">8</div>
                        </div>
                        <div className="text-sm">
                           <div>years of</div>
                           <div>Excellence in</div>
                           <div>Accounting Tech</div>
                        </div>
                     </div>
                  </div>
               </div>

               {/* Right Side - Content */}
               <div>
                  <div className="text-blue-600 text-sm font-semibold mb-4 tracking-wide uppercase">About Zeronine</div>

                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                     Revolutionizing <span className="text-blue-600 font-black">Accounting Software</span> For Modern
                     Businesses
                  </h2>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                     At Zeronine, we're redefining accounting technology with innovative software solutions that
                     streamline financial management for businesses of all sizes. Our cloud-based platform combines
                     cutting-edge automation with intuitive design, making complex accounting tasks simple and
                     efficient.
                  </p>

                  <p className="text-gray-600 mb-8 leading-relaxed">
                     With over 5,000 satisfied customers worldwide, our AI-powered features like real-time financial
                     analytics, automated tax compliance, and seamless integration with banking systems have positioned
                     Zeronine as a leader in the accounting software industry. We're committed to helping businesses
                     achieve financial clarity and growth through technology.
                  </p>

                  {/* Additional Company Highlights */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                     <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-700">Cloud-Based Solutions</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-700">AI-Powered Analytics</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-700">Real-Time Reporting</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-700">24/7 Customer Support</span>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
   );

   const renderAdvancedFeatures = () => (
      <section className="py-24 px-4 bg-[#FBFBFB]">
         <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
               <div className="inline-block px-6 py-2 bg-blue-50 rounded-full mb-4">
                  <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Next-Gen Features</span>
               </div>
               <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Powered by Advanced
                  <span className="block text-blue-600 mt-2">AI Technology</span>
               </h2>
               <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Experience the future of accounting with cutting-edge artificial intelligence and machine learning
               </p>
            </div>

            {/* Feature Tabs */}
            <div className="flex justify-center mb-16">
               <div className="inline-flex bg-gray-100 rounded-xl p-1.5 gap-1">
                  <button
                     onClick={() => setActiveFeatureTab('ai')}
                     className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                        activeFeatureTab === 'ai'
                           ? 'bg-white text-gray-900 shadow-md'
                           : 'text-gray-600 hover:text-gray-900'
                     }`}>
                     🤖 AI Features
                  </button>
                  <button
                     onClick={() => setActiveFeatureTab('security')}
                     className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                        activeFeatureTab === 'security'
                           ? 'bg-white text-gray-900 shadow-md'
                           : 'text-gray-600 hover:text-gray-900'
                     }`}>
                     🔒 Security
                  </button>
                  <button
                     onClick={() => setActiveFeatureTab('analytics')}
                     className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                        activeFeatureTab === 'analytics'
                           ? 'bg-white text-gray-900 shadow-md'
                           : 'text-gray-600 hover:text-gray-900'
                     }`}>
                     📊 Analytics
                  </button>
               </div>
            </div>

            {/* AI Features Grid */}
            {activeFeatureTab === 'ai' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {aiFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300">
                        <div
                           className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                           <feature.icon className="w-7 h-7 text-white" />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 leading-relaxed">{feature.description}</p>

                        <div className="space-y-2 mb-4">
                           {feature.features.map((feat, i) => (
                              <div key={i} className="flex items-start gap-2">
                                 <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                 <span className="text-xs text-gray-600">{feat}</span>
                              </div>
                           ))}
                        </div>

                        <div className="flex items-center gap-2 text-sm font-semibold text-blue-600 pt-3 border-t border-gray-100">
                           <TrendingUp className="w-4 h-4" />
                           {feature.stats}
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {/* Security Features Grid */}
            {activeFeatureTab === 'security' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {securityFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-red-400 hover:shadow-xl transition-all duration-300">
                        <div
                           className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                           <feature.icon className="w-7 h-7 text-white" />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 leading-relaxed">{feature.description}</p>

                        <div className="space-y-2 mb-4">
                           {feature.features.map((feat, i) => (
                              <div key={i} className="flex items-start gap-2">
                                 <ShieldCheck className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                 <span className="text-xs text-gray-600">{feat}</span>
                              </div>
                           ))}
                        </div>

                        <div className="flex items-center gap-2 text-sm font-semibold text-red-600 pt-3 border-t border-gray-100">
                           <Lock className="w-4 h-4" />
                           {feature.stats}
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {/* Analytics Features Grid */}
            {activeFeatureTab === 'analytics' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {analyticsFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300">
                        <div
                           className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                           <feature.icon className="w-7 h-7 text-white" />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 leading-relaxed">{feature.description}</p>

                        <div className="space-y-2 mb-4">
                           {feature.features.map((feat, i) => (
                              <div key={i} className="flex items-start gap-2">
                                 <BarChart className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                 <span className="text-xs text-gray-600">{feat}</span>
                              </div>
                           ))}
                        </div>

                        <div className="flex items-center gap-2 text-sm font-semibold text-blue-600 pt-3 border-t border-gray-100">
                           <TrendingUp className="w-4 h-4" />
                           {feature.stats}
                        </div>
                     </div>
                  ))}
               </div>
            )}
         </div>
      </section>
   );

   // Filter active packages and remove duplicates based on name

   // Get icon based on business type
   const getIcon = (businessTypeName) => {
      const iconMap = {
         MOBILE: Smartphone,
         TEXTILE: Shirt,
         NORMAL: Zap,
         EMBROIDERY: Scissors,
         DEFAULT: Zap,
      };
      return iconMap[businessTypeName] || iconMap.DEFAULT;
   };
   // Check if discount exists
   const hasDiscount = (pkg) => {
      return (pkg.discPer && pkg.discPer > 0) || (pkg.discAmt && pkg.discAmt > 0);
   };

   // Better approach - show actual duration without conversion:
   const getPriceBasedOnDurType = (pkg) => {
      // Always return the actual AMC price, let duration text handle the period
      return pkg.amcPrice;
   };

   // Calculate original price (before discount) - UPDATED
   const getOriginalPrice = (pkg) => {
      const basePrice = getPriceBasedOnDurType(pkg);

      if (pkg.discPer && pkg.discPer > 0) {
         return Math.round(basePrice / (1 - pkg.discPer / 100));
      }

      if (pkg.discAmt && pkg.discAmt > 0) {
         return Math.round(basePrice + pkg.discAmt);
      }

      return basePrice;
   };

   // Get final price (after discount) - UPDATED
   const getFinalPrice = (pkg) => {
      return getPriceBasedOnDurType(pkg);
   };

   const getDurationText = (pkg) => {
      const durationType = pkg.durType?.toLowerCase();
      const amcPeriod = pkg.amcPeriod;

      if (!durationType && !amcPeriod) {
         return billingCycle === 'monthly' ? '/month' : '/year';
      }

      // Handle days specifically
      if (durationType === 'days' || durationType === 'day' || durationType === 'daily') {
         if (amcPeriod === 1) return '/day';
         if (amcPeriod === 7) return '/week';
         if (amcPeriod === 15) return '/15 days';
         if (amcPeriod === 30) return '/month';
         return `/${amcPeriod} days`;
      }

      // Handle months
      if (durationType === 'months' || durationType === 'month' || durationType === 'monthly') {
         if (amcPeriod === 1) return '/month';
         if (amcPeriod === 3) return '/quarter';
         if (amcPeriod === 6) return '/half-year';
         if (amcPeriod === 12) return '/year';
         return `/${amcPeriod} months`;
      }

      // Handle years
      if (
         durationType === 'years' ||
         durationType === 'year' ||
         durationType === 'yearly' ||
         durationType === 'annual'
      ) {
         if (amcPeriod === 1) return '/year';
         return `/${amcPeriod} years`;
      }

      // Default case - show both type and period
      return `/${amcPeriod || ''} ${durationType || ''}`.trim();
   };

   // Get discount percentage text
   const getDiscountText = (pkg) => {
      if (pkg.discPer && pkg.discPer > 0) {
         return `${pkg.discPer}% OFF`;
      }
      if (pkg.discAmt && pkg.discAmt > 0) {
         const originalPrice = getOriginalPrice(pkg);
         const discountPercentage = Math.round((pkg.discAmt / originalPrice) * 100);
         return `${discountPercentage}% OFF`;
      }
      return null;
   };

   const scrollToSection = (section) => {
      setMobileMenuOpen(false);

      // If not on home page, navigate to home first
      if (currentPage !== 'home') {
         window.location.href = `/#${section}`;
         return;
      }

      // If on home page, scroll to section
      setActiveSection(section);
      const element = document.getElementById(section);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth' });
      }
   };

   // Get discount amount
   const getDiscountAmount = (pkg) => {
      const originalPrice = getOriginalPrice(pkg);
      const finalPrice = getFinalPrice(pkg);
      return originalPrice - finalPrice;
   };

   const formatValue = (value) => {
      if (value === -100) return 'Unlimited';
      if (value === null || value === undefined) return '-';
      return typeof value === 'number' ? value.toLocaleString() : value;
   };

   // Helper function to format unlimited values
   const formatUnlimited = (value, suffix) => {
      if (value === -100) return `Unlimited ${suffix}`;
      return `${value.toLocaleString()} ${suffix}`;
   };

   // Generate features array based on package data
   const generateFeatures = (pkg) => {
      const features = [];

      // Basic features
      if (pkg.noOfUser) {
         features.push(formatUnlimited(pkg.noOfUser, 'Users')); // Remove { text: ... }
      }

      if (pkg.noOfCompanies) {
         features.push(formatUnlimited(pkg.noOfCompanies, 'Companies'));
      }

      if (pkg.noOfWp) {
         features.push(formatUnlimited(pkg.noOfWp, 'Daily WhatsApp'));
      }

      if (pkg.noOfEInvoice) {
         features.push(formatUnlimited(pkg.noOfEInvoice, 'E-Invoices'));
      }

      if (pkg.noOfEways) {
         features.push(formatUnlimited(pkg.noOfEways, 'E-Way Bills'));
      }

      // Feature flags - Remove all { text: ... } wrappers
      if (pkg.isCrm) {
         features.push('CRM System');
      }

      if (pkg.isToDo) {
         features.push('To-Do Management');
      }

      if (pkg.isMobileApp) {
         features.push('Mobile App Access');
      }

      if (pkg.isDashboardGst) {
         features.push('GST Dashboard');
      }

      if (pkg.is2stepVerify) {
         features.push('2-Step Verification');
      }

      if (pkg.is2a2b) {
         features.push('2A/2B Matching');
      }

      if (pkg.isDailyWp) {
         features.push('Daily Waybill Generation');
      }

      // New features from backend - Remove all { text: ... } wrappers
      if (pkg.sales_order) {
         features.push('Sales Order');
      }

      if (pkg.purchase_order) {
         features.push('Purchase Order');
      }

      if (pkg.sales_invoice) {
         features.push('Sales Invoice');
      }

      if (pkg.purchase_challan) {
         features.push('Purchase Challan');
      }

      if (pkg.purchase_invoice) {
         features.push('Purchase Invoice');
      }

      if (pkg.sales_return) {
         features.push('Sales Return');
      }

      if (pkg.purchase_return) {
         features.push('Purchase Return');
      }

      if (pkg.issue) {
         features.push('Issue Management');
      }

      if (pkg.receive) {
         features.push('Receive Management');
      }

      if (pkg.cash) {
         features.push('Cash Management');
      }

      if (pkg.bank) {
         features.push('Bank Management');
      }

      if (pkg.jv) {
         features.push('Journal Voucher');
      }

      if (pkg.stock_transfer) {
         features.push('Stock Transfer');
      }

      if (pkg.share_company) {
         features.push('Share Company');
      }

      if (pkg.company_wise_master) {
         features.push('Company-wise Master');
      }

      if (pkg.delete_unused_master) {
         features.push('Delete Unused Master');
      }

      if (pkg.aamne_saamne_sale_purchase) {
         features.push('Aamne Saamne Sale/Purchase');
      }

      if (pkg.multiple_eway_bill_generate) {
         features.push('Multiple E-way Bill');
      }

      if (pkg.print_barcode) {
         features.push('Print Barcode');
      }

      if (pkg.bank_import) {
         features.push('Bank Import');
      }

      if (pkg.bulk_update_suspense_account) {
         features.push('Bulk Update Suspense Account');
      }

      // Products included
      if (pkg.product && pkg.product.length > 0) {
         const productCount = pkg.product.length;
         features.push(`${productCount} Product Modules`);
      }

      // AMC Period
      if (pkg.amcPeriod) {
         features.push(`${pkg.amcPeriod} Months AMC`);
      }

      return features.slice(0, 8);
   };

   const scrollToNextSection = () => {
      // Get all sections with IDs
      const sections = ['home', 'features', 'pricing', 'testimonials'];
      const currentScroll = window.scrollY;

      // Find the current section
      for (let i = 0; i < sections.length; i++) {
         const section = document.getElementById(sections[i]);
         if (section) {
            const sectionTop = section.offsetTop;
            const sectionBottom = sectionTop + section.offsetHeight;

            // If we're in this section, scroll to next one
            if (currentScroll >= sectionTop && currentScroll < sectionBottom) {
               if (i + 1 < sections.length) {
                  scrollToSection(sections[i + 1]);
               }
               break;
            }
         }
      }
   };

   return (
      <>
         <div className={`min-h-screen bg-white transition-colors duration-300 ${darkMode ? 'dark' : ''}`}>
            <Navigation />
            <>
               {/* Hero Section - Ultra Modern */}
               <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 pt-20">
                  {/* Animated Background */}
                  <div className="absolute inset-0">
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%)]"></div>
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(99,102,241,0.3),transparent_50%)]"></div>
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_80%,rgba(168,85,247,0.2),transparent_50%)]"></div>

                     {/* Floating Elements */}
                     <div
                        className="absolute w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
                        style={{
                           left: `${mousePosition.x / 50}px`,
                           top: `${mousePosition.y / 50}px`,
                           transition: 'all 0.3s ease-out',
                        }}></div>
                     <div
                        className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
                        style={{
                           right: `${mousePosition.x / 80}px`,
                           bottom: `${mousePosition.y / 80}px`,
                           transition: 'all 0.5s ease-out',
                        }}></div>

                     {/* Grid Pattern */}
                     <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

                     {/* Animated Particles */}
                     <div className="absolute inset-0 opacity-30">
                        {[...Array(20)].map((_, i) => (
                           <div
                              key={i}
                              className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
                              style={{
                                 left: `${Math.random() * 100}%`,
                                 top: `${Math.random() * 100}%`,
                                 animationDelay: `${Math.random() * 5}s`,
                              }}
                           />
                        ))}
                     </div>
                  </div>

                  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
                     {/* Floating Badge */}
                     <div className="inline-flex items-center gap-3 mb-8 px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 shadow-2xl animate-pulse">
                        <div className="flex -space-x-2">
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 border-2 border-white"></div>
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 border-2 border-white"></div>
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 border-2 border-white"></div>
                        </div>
                        <span className="text-white font-bold">500+ businesses trust us</span>
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                     </div>

                     {/* Main Headline */}
                     <h1 className="text-6xl md:text-8xl font-black text-white mb-2 leading-tight">
                        The Future of
                        <span className="block bg-gradient-to-r pb-3 from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                           Business Accounting
                        </span>
                     </h1>

                     <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
                        Say goodbye to complex spreadsheets. Manage invoices, GST, inventory, and payments with India's
                        most powerful AI-driven accounting platform.
                     </p>

                     {/* CTA Buttons */}
                     <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
                        <button className="group relative px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden shadow-2xl hover:shadow-blue-500/50 transition-all">
                           <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600"></div>
                           <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-all"></div>
                           <button onClick={() => scrollToSection('pricing')}>
                              <span className="relative text-white flex items-center gap-3">
                                 <Rocket className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                                 Start Free Trial
                                 <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                              </span>
                           </button>
                        </button>
                     </div>

                     {/* Trust Indicators */}
                     <div className="flex flex-wrap justify-center gap-8 text-white/80">
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>No credit card required</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>Cancel anytime</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>AI-powered setup</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>Voice commands</span>
                        </div>
                     </div>

                     {/* Floating Dashboard Preview */}
                     <div className="mt-20 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10"></div>
                        <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-4 shadow-2xl transform hover:scale-105 transition-all duration-500">
                           <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-2xl aspect-video flex items-center justify-center">
                              <Image src={Dashboard} alt="dashboard" w={'full'} h={'full'} />
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Scroll Indicator */}
                  <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
                     <button
                        onClick={() => {
                           window.scrollBy({
                              top: window.innerHeight - 100,
                              behavior: 'smooth',
                           });
                        }}
                        className="animate-bounce bg-white/10 backdrop-blur-md p-4 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer group z-50"
                        style={{ zIndex: 9999 }}>
                        <ChevronDown className="w-8 h-8 text-white/80 group-hover:text-white" />
                     </button>
                  </div>
               </section>

               {/* Logo Cloud */}
               <section className="py-16 bg-white border-y border-gray-200">
                  <div className="max-w-7xl mx-auto px-4">
                     <p className="text-center text-gray-500 font-semibold mb-8">TRUSTED BY LEADING BUSINESSES</p>
                     <div className="flex flex-wrap justify-center items-center gap-12 opacity-50">
                        {['Parmer Enterprise', 'Foreign Mobile', 'Shreeji Enterprise'].map((name, idx) => (
                           <div key={idx} className="text-2xl font-black text-gray-400">
                              {name}
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {renderAboutSection()}
               {/* Advanced Features Section */}
               {renderAdvancedFeatures()}

               {/* Features - Bento Grid Style */}
               <section id="features" className="py-32 px-4 bg-gradient-to-b from-white to-gray-50">
                  <div className="max-w-7xl mx-auto">
                     <div className="text-center mb-14">
                        <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-6">
                           <span className="text-blue-600 font-bold">✨ POWERFUL FEATURES</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-3">
                           Everything You Need,
                           <span className="block bg-gradient-to-r pb-2 from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                              Nothing You Don't
                           </span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                           Powerful features that help you manage your business better with AI assistance
                        </p>
                     </div>

                     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, idx) => (
                           <div
                              key={idx}
                              className="group relative bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden">
                              <div
                                 className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}></div>

                              {feature.advanced && (
                                 <div className="absolute top-4 right-4">
                                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                       <Sparkles className="w-3 h-3" />
                                       AI Powered
                                    </div>
                                 </div>
                              )}

                              <div className="relative">
                                 <div
                                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                                    <feature.icon className="w-8 h-8 text-white" />
                                 </div>

                                 <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                 <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>

                                 <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                                    <TrendingUp className="w-4 h-4" />
                                    {feature.stats}
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {/* Pricing - Interactive Comparison */}
               <section
                  id="pricing"
                  className="py-32 px-4 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>

                  <div className="relative max-w-7xl mx-auto">
                     <div className="text-center mb-8">
                        <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full mb-6 border border-white/20">
                           <span className="text-white font-bold">💰 SIMPLE PRICING</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-white mb-3">Choose Your Perfect Plan</h2>
                        <p className="text-xl text-gray-300 mb-8">
                           Transparent pricing. No hidden fees. Cancel anytime.
                        </p>
                     </div>

                     {/* Business Type Filter */}
                     {getAvailableBusinessTypes().length > 1 && (
                        <div className="flex justify-center mb-10">
                           <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-2 border border-white/20">
                              <div className="flex flex-wrap gap-2 justify-center">
                                 {getAvailableBusinessTypes().map((businessType) => (
                                    <button
                                       key={businessType}
                                       onClick={() => setSelectedBusinessType(businessType)}
                                       className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                                          selectedBusinessType === businessType
                                             ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                                             : 'text-white/70 hover:text-white hover:bg-white/10'
                                       }`}>
                                       {businessType}
                                    </button>
                                 ))}
                              </div>
                           </div>
                        </div>
                     )}

                     <div className="relative mb-16">
                        {/* Navigation Arrows */}
                        {filteredPackages.length > 3 && (
                           <>
                              <button
                                 onClick={() => scrollPricing('left')}
                                 className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 bg-white/20 backdrop-blur-xl rounded-full p-3 border border-white/30 hover:bg-white/30 transition-all duration-300 hidden md:flex items-center justify-center">
                                 <ChevronLeft className="w-6 h-6 text-white" />
                              </button>
                              <button
                                 onClick={() => scrollPricing('right')}
                                 className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 bg-white/20 backdrop-blur-xl rounded-full p-3 border border-white/30 hover:bg-white/30 transition-all duration-300 hidden md:flex items-center justify-center">
                                 <ChevronRight className="w-6 h-6 text-white" />
                              </button>
                           </>
                        )}

                        {/* Carousel Container */}
                        <div
                           ref={carouselRef}
                           className="flex overflow-x-auto scrollbar-hide space-x-6 pb-8 px-8 md:px-0 snap-x snap-mandatory"
                           style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                           {filteredPackages.length > 0 ? (
                              filteredPackages.map((pkg, idx) => {
                                 const Icon = getIcon(pkg.businesstypename);
                                 const features = generateFeatures(pkg);
                                 const isSelectedOrHovered = selectedPlan === idx;
                                 const durationText = getDurationText(pkg);

                                 return (
                                    <div
                                       key={pkg.id}
                                       onClick={() => handlePlanSelect(idx)}
                                       onMouseEnter={() => setHoveredPlan(idx)}
                                       onMouseLeave={() => setHoveredPlan(null)}
                                       className={`
                  flex-none w-80 md:w-96 mt-8 rounded-3xl p-8 transition-all duration-300 cursor-pointer snap-center relative
                  ${
                     isSelectedOrHovered
                        ? 'bg-white shadow-2xl scale-105 border-4 border-blue-500'
                        : 'bg-white/5 backdrop-blur-xl border-2 border-white/10 hover:border-white/30'
                  }
                `}>
                                       {/* Selected/Hovered Badge */}
                                       {isSelectedOrHovered && (
                                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                             <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                                                <Star className="w-4 h-4 fill-current" />
                                                {selectedPlan === idx ? 'Selected Plan' : ''}
                                             </div>
                                          </div>
                                       )}

                                       <div className="mb-6">
                                          <Icon
                                             className={`w-12 h-12 mb-4 ${
                                                isSelectedOrHovered ? 'text-blue-600' : 'text-white'
                                             }`}
                                          />
                                          <h3
                                             className={`text-3xl font-black mb-2 ${
                                                isSelectedOrHovered ? 'text-gray-900' : 'text-white'
                                             }`}>
                                             {pkg.name || `Plan ${idx + 1}`}
                                          </h3>
                                          <p
                                             className={`text-sm ${
                                                isSelectedOrHovered ? 'text-gray-600' : 'text-gray-400'
                                             }`}>
                                             {pkg.detail || 'Premium features for your business'}
                                          </p>
                                       </div>

                                       <div className="mb-8">
                                          {/* Original Price with Strike-through (if discount exists) */}
                                          {hasDiscount(pkg) && (
                                             <div className="flex items-center justify-center gap-2 mb-2">
                                                <span
                                                   className={`text-2xl font-bold line-through ${
                                                      isSelectedOrHovered ? 'text-gray-400' : 'text-white/40'
                                                   }`}>
                                                   ₹{getOriginalPrice(pkg).toLocaleString()}
                                                </span>
                                                <span
                                                   className={`text-sm font-bold px-2 py-1 rounded ${
                                                      isSelectedOrHovered
                                                         ? 'bg-green-100 text-green-600'
                                                         : 'bg-green-500/20 text-green-300'
                                                   }`}>
                                                   Save ₹{getDiscountAmount(pkg).toLocaleString()}
                                                </span>
                                             </div>
                                          )}

                                          {/* Final Price */}
                                          <div className="flex items-baseline gap-2 justify-center">
                                             <span
                                                className={`text-6xl font-black ${
                                                   isSelectedOrHovered ? 'text-gray-900' : 'text-white'
                                                }`}>
                                                ₹{getFinalPrice(pkg).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-xl font-bold ${
                                                   isSelectedOrHovered ? 'text-gray-600' : 'text-white/60'
                                                }`}>
                                                {getDurationText(pkg)}
                                             </span>
                                          </div>
                                       </div>

                                       {/* Features List */}
                                       <div className="mb-8 space-y-4">
                                          {features &&
                                             Array.isArray(features) &&
                                             features.slice(0, 5).map((feature, featureIdx) => (
                                                <div key={featureIdx} className="flex items-center gap-3">
                                                   <Check
                                                      className={`w-5 h-5 ${
                                                         isSelectedOrHovered ? 'text-green-600' : 'text-green-400'
                                                      }`}
                                                   />
                                                   <span
                                                      className={`text-sm ${
                                                         isSelectedOrHovered ? 'text-gray-700' : 'text-gray-300'
                                                      }`}>
                                                      {feature}
                                                   </span>
                                                </div>
                                             ))}
                                       </div>

                                       <Link to={`/auth/registration?package=${pkg.id}`}>
                                          <button
                                             className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform hover:scale-105 ${
                                                isSelectedOrHovered
                                                   ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl hover:shadow-2xl'
                                                   : 'bg-white/10 backdrop-blur-xl border-2 border-white/20 text-white hover:bg-white/20'
                                             }`}>
                                             Get Started
                                          </button>
                                       </Link>
                                    </div>
                                 );
                              })
                           ) : (
                              <div className="flex-none w-80 md:w-96 mt-8 rounded-3xl p-8 bg-white/5 backdrop-blur-xl border-2 border-white/10 text-center">
                                 <div className="text-white text-lg">
                                    No packages available for {selectedBusinessType}
                                 </div>
                              </div>
                           )}
                        </div>

                        {/* Dots Indicator */}
                        {filteredPackages.length > 1 && (
                           <div className="flex justify-center space-x-2 mt-6">
                              {filteredPackages.map((_, idx) => (
                                 <button
                                    key={idx}
                                    onClick={() => scrollToPricingCard(idx)}
                                    className={`w-3 h-3 rounded-full transition-all ${
                                       selectedPlan === idx ? 'bg-white scale-125' : 'bg-white/30'
                                    }`}
                                 />
                              ))}
                           </div>
                        )}
                     </div>

                     {/* Comparison Table - Show if there's at least 1 package */}
                     {filteredPackages.length >= 1 && (
                        <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 overflow-x-auto">
                           <h3 className="text-2xl font-bold text-white mb-6 text-center">
                              Detailed Feature Comparison
                           </h3>
                           <table className="w-full min-w-[800px]">
                              <thead>
                                 <tr className="border-b border-white/10">
                                    <th className="text-left py-4 text-white font-bold">Feature</th>
                                    {filteredPackages.map((pkg) => (
                                       <th key={pkg.id} className="text-center py-4 text-white font-bold">
                                          {pkg.name || 'Plan'}
                                       </th>
                                    ))}
                                 </tr>
                              </thead>
                              <tbody>
                                 {/* Basic Features */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Users</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfUser === -100 ? 'text-green-400 font-semibold' : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfUser)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Companies</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfCompanies === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfCompanies)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Daily WhatsApp</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfWp === -100 ? 'text-green-400 font-semibold' : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfWp)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">E-Invoices</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfEInvoice === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfEInvoice)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">E-Way Bills</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfEways === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfEways)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>

                                 {/* Feature Flags */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">CRM System</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isCrm ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">To-Do Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isToDo ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Mobile App Access</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isMobileApp ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">GST Dashboard</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isDashboardGst ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">2-Step Verification</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.is2stepVerify ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">2A/2B Matching</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.is2a2b ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>

                                 {/* Additional Features */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Order</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_order ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Order</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_order ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Invoice</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_invoice ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Challan</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_challan ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Invoice</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_invoice ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Return</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_return ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Return</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_return ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Issue Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.issue ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Receive Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.receive ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Cash Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.cash ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bank Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bank ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Journal Voucher</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.jv ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Stock Transfer</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.stock_transfer ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Share Company</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.share_company ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Company-wise Master</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.company_wise_master ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Delete Unused Master</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.delete_unused_master ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Aamne Saamne Sale/Purchase</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.aamne_saamne_sale_purchase ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Multiple E-way Bill</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.multiple_eway_bill_generate ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Print Barcode</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.print_barcode ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bank Import</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bank_import ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bulk Update Suspense Account</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bulk_update_suspense_account ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     )}
                  </div>
               </section>

               {/* Testimonials - Modern Cards */}
               <section id="testimonials" className="py-32 px-4 bg-white">
                  <div className="max-w-7xl mx-auto">
                     <div className="text-center mb-20">
                        <div className="inline-block px-4 py-2 bg-yellow-100 rounded-full mb-6">
                           <span className="text-yellow-600 font-bold">⭐ CUSTOMER STORIES</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Loved by 500+ Businesses</h2>
                        <p className="text-xl text-gray-600">Real results from real businesses using AI features</p>
                     </div>

                     <div className="grid md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, idx) => (
                           <div
                              key={idx}
                              className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-300">
                              <div className="absolute top-6 right-6 flex gap-1">
                                 {[...Array(testimonial.rating)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                 ))}
                              </div>

                              <div className="flex items-center gap-4 mb-6">
                                 <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-50"></div>
                                    <div className="relative w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
                                       {testimonial.image}
                                    </div>
                                 </div>
                                 <div>
                                    <p className="font-black text-gray-900 text-lg">{testimonial.name}</p>
                                    <p className="text-sm text-gray-600 font-semibold">{testimonial.role}</p>
                                    <p className="text-xs text-gray-500">{testimonial.company}</p>
                                 </div>
                              </div>

                              <p className="text-gray-700 text-lg mb-6 leading-relaxed italic">"{testimonial.text}"</p>

                              <div className="space-y-3">
                                 <div className="pt-4 border-t border-blue-200">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
                                       <TrendingUp className="w-4 h-4 text-green-600" />
                                       <span className="text-sm font-bold text-green-600">{testimonial.revenue}</span>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-2 text-sm text-blue-600">
                                    <Bot className="w-4 h-4" />
                                    <span className="font-medium">{testimonial.aiUsage}</span>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>

                     <div className="mt-16 text-center">
                        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-100 rounded-full">
                           <div className="flex gap-1">
                              {[...Array(5)].map((_, i) => (
                                 <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                              ))}
                           </div>
                           <span className="font-bold text-gray-900">4.9/5 from 100+ reviews</span>
                        </div>
                     </div>
                  </div>
               </section>

               {/* FAQ Section */}
               <section className="py-32 px-4 bg-gradient-to-b from-gray-50 to-white">
                  <div className="max-w-4xl mx-auto">
                     <div className="text-center mb-16">
                        <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-6">
                           <span className="text-purple-600 font-bold">💬 FAQ</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Got Questions?</h2>
                        <p className="text-xl text-gray-600">We've got answers about AI features and more</p>
                     </div>

                     <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                           <div
                              key={idx}
                              className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-blue-600 font-bold">{idx + 1}</span>
                                 </div>
                                 {faq.q}
                              </h3>
                              <p className="text-gray-600 ml-11 leading-relaxed">{faq.a}</p>
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {/* Final CTA - Bold & Eye-catching */}
               <section className="py-32 px-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

                  <div className="relative max-w-5xl mx-auto text-center text-white">
                     <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-xl rounded-full mb-8 border border-white/30">
                        <Clock className="w-5 h-5" />
                        <span className="font-bold">Limited Time Offer</span>
                     </div>

                     <h2 className="text-5xl md:text-7xl font-black mb-8">Ready to Transform Your Business?</h2>

                     <p className="text-2xl mb-12 text-blue-100 leading-relaxed">
                        Join 50,000+ businesses already using ZeroNine AI.
                        <br />
                        Start your free trial today. No credit card required.
                     </p>

                     <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
                        <button className="group relative px-12 py-6 rounded-2xl font-black text-xl overflow-hidden shadow-2xl hover:shadow-white/20 transition-all">
                           <div className="absolute inset-0 bg-white"></div>
                           <button onClick={() => scrollToSection('pricing')}>
                              <span className="relative bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center justify-center gap-3">
                                 <Rocket className="w-6 h-6 text-blue-600 group-hover:rotate-12 transition-transform" />
                                 Start Free Trial
                                 <ArrowRight className="w-6 h-6 text-blue-600 group-hover:translate-x-2 transition-transform" />
                              </span>
                           </button>
                        </button>
                     </div>
                  </div>
               </section>
            </>
            <Footer />

            {/* Back to Top Button */}
            {showBackToTop && (
               <button
                  onClick={scrollToTop}
                  className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-all z-40 group animate-bounce">
                  <ArrowUp className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
               </button>
            )}
         </div>
      </>
   );
}
